import React from 'react'

import { PopoverTrigger, getExtendedDialogState, usePopoverState } from '../../../components'
import { useModelState } from '../../../model-hooks/use-model-state'
import ChartModel from '../../../models/chart'
import { useClickAway } from '../../../utils/use-click-away'
import { ChartEventPopover } from './chart-event-popover'

interface ChartEventPopoverWithStateProps {
  chartModel: ChartModel
}

const POPOVER_ID = 'badge-popover'

export function ChartEventPopoverWithState({ chartModel }: ChartEventPopoverWithStateProps) {
  const unwatchedChartLayout = React.useMemo(() => chartModel.chart_layout() ?? null, [chartModel])
  const { activeChartEvent, theme } = useModelState(unwatchedChartLayout, { watchProperties: ['activeChartEvent'] })
  const popoverState = usePopoverState({ placement: 'top' })
  const { isFullyClosed } = getExtendedDialogState(popoverState)
  const [style, setStyle] = React.useState({
    top: 0,
    left: 0,
  })
  const elementId = activeChartEvent?.elementId
  const isChartEventOfThisChart = activeChartEvent?.pane().chart().eql(chartModel)
  const [data, setData] = React.useState<ObjectHash | null>(null)
  const clickAwayRef = useClickAway(() => {
    const isAnyBadgeHovered = !!chartModel
      .getChartPane()
      ?.getAllChartEvents()
      .some((chartEvent) => chartEvent.instance.isHovered)

    if (!isAnyBadgeHovered && activeChartEvent && isChartEventOfThisChart) {
      activeChartEvent.instance.toggleIsOpen(false)
    }
  }, !!activeChartEvent && isChartEventOfThisChart)
  // This is intentional, it doesn't need to be in useEffect, it would just call effect function which is redundant
  clickAwayRef.current = popoverState.unstable_popoverRef.current
  const popoverShow = popoverState.show
  const popoverHide = popoverState.hide

  React.useEffect(() => {
    if (!activeChartEvent?.instance.isOpen || !isChartEventOfThisChart) {
      if (isFullyClosed) {
        return setData(null)
      }
      return popoverHide()
    }
    if (!isChartEventOfThisChart) return
    const { top, left } = activeChartEvent.instance
    const newData = activeChartEvent.getChartEventData()
    if (newData?.elementId !== data?.elementId) {
      setData({
        ...newData,
        badgeType: activeChartEvent.instance.getChartEvenBadgeType(),
      })
    }

    if (style.top !== top || style.left !== left) {
      popoverHide()
      setStyle({
        top,
        left,
      })
    }
    if (!popoverState.visible) {
      popoverShow()
    }
  }, [
    elementId,
    isFullyClosed,
    data,
    style,
    popoverShow,
    popoverHide,
    popoverState.visible,
    activeChartEvent,
    chartModel,
    isChartEventOfThisChart,
  ])

  return (
    <>
      <PopoverTrigger as="div" state={popoverState} className="absolute" style={style} />
      {data && (
        <ChartEventPopover
          id={POPOVER_ID}
          popoverState={popoverState}
          badgeType={data.badgeType}
          data={data}
          theme={theme}
        />
      )}
    </>
  )
}
